import { Grid, List, ListItem, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { read, WorkSheet, utils } from 'xlsx';

const getColor = (props: any) => {
  if (props.isDragAccept) {
    return props.theme.palette.sptGreen.main;
  }
  if (props.isDragReject) {
    return props.theme.palette.sptPurple.main;
  }
  if (props.isFocused) {
    return props.theme.palette.primary.main;
  }
  return props.theme.palette.sptGrey.main;
};

const Dropzone = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props: any) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
`;

interface AlandDataImport {
  onImport: (ws: WorkSheet, files: File[]) => void;
  files: File[];
}

interface AlandExcelRow {
  kunta: string;           // Kommun from B4
  vuosi: number;           // Raporteringsår from B6  
  kuukausi: string;        // Månad from A13 onwards
  keraystyyppi: string;    // from B13 onwards
  materiaali: string;      // from C13 onwards
  keraysvaline: string;    // from D13 onwards
  tyhjennystenmaara: number;   // from E13 onwards
  tyhjennyksenyksikkohinta: string;    // from F13 onwards - formatted with 3 decimals
}

const AlandDataImport = ({ onImport, files }: AlandDataImport) => {
  const { t } = useTranslation();

  const processAlandData = (ws: WorkSheet): AlandExcelRow[] => {
    // Extract metadata from specific cells
    const kunta = ws['B4']?.v || '';
    const vuosi = ws['B6']?.v || 0;

    // Get data starting from row 13, columns A-F
    const range = utils.decode_range(ws['!ref'] || 'A1:F1000');
    range.s.r = 12; // Start from row 13 (0-indexed, so 12)
    range.e.c = 5;  // End at column F (0-indexed, so 5)
    
    // Create a new worksheet reference with the adjusted range
    const dataRange = utils.encode_range(range);
    const tempWs = { ...ws, '!ref': dataRange };
    
    // Extract the actual data rows as raw array
    const rawData = utils.sheet_to_json<any[]>(tempWs, { 
      raw: true,
      range: dataRange,
      header: 1 // Use array format instead of object
    });

    // Process each row and map to our specific structure
    // Stop when we hit the first completely empty row
    const processedData: AlandExcelRow[] = [];
    
    for (const row of rawData) {
      // Check if all cells in the row are empty
      const isEmpty = !row[0] && !row[1] && !row[2] && !row[3] && !row[4] && !row[5];
      
      if (isEmpty) {
        console.log('Found empty row, stopping data processing');
        break;
      }
      
      // Only process rows that have at least some data
      processedData.push({
        kunta,                           // From B4
        vuosi,                           // From B6
        kuukausi: row[0] || '',          // Column A (index 0)
        keraystyyppi: row[1] || '',      // Column B (index 1)
        materiaali: row[2] || '',        // Column C (index 2)
        keraysvaline: row[3] || '',      // Column D (index 3)
        tyhjennystenmaara: Number(row[4]) || 0,  // Column E (index 4)
        tyhjennyksenyksikkohinta: (Number(row[5]) || 0).toFixed(3)    // Column F (index 5) - always 3 decimals as string
      });
    }

    return processedData;
  };

  const onDrop = (acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.onload = () => {
        const binaryStr = reader.result;
        const workbook = read(binaryStr, { cellDates: false });
        const ws = workbook.Sheets[workbook.SheetNames[0]];
        
        const processedData = processAlandData(ws);
        
        // Convert back to worksheet for consistency with the interface
        const newWs = utils.json_to_sheet(processedData);
        onImport(newWs, acceptedFiles);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
  });

  const hasFiles = Boolean(files.length);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Dropzone
            style={{
              minHeight: '8rem',
              borderRadius: '0.7rem',
              color: !hasFiles ? '#9d9d9d' : 'black',
              cursor: 'pointer',
            }}
            {...getRootProps({ isFocused, isDragAccept, isDragReject })}
          >
            <input {...getInputProps()} />
            {!hasFiles && <p>{t('excelImport.fileImport.text')}</p>}
            {hasFiles && (
              <Grid item xs={12}>
                <List>
                  {files.map((file: File) => (
                    <ListItem sx={{ pl: 0, flexDirection: 'column' }} key={file.name}>
                      <Typography>{file.name}</Typography>
                      <Typography>{file.size} bytes</Typography>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
          </Dropzone>
        </Grid>
      </Grid>
    </>
  );
};

export default AlandDataImport; 